import { useState, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui';
import { useModel, useUpdateModelConfig } from '@/hooks/use-models-v2';
import { SensitivityTab } from './SensitivityTab';

interface ModelConfiguratorProps {
  modelId: string;
  onClose: () => void;
}

type Tab = 'outputs' | 'inputs' | 'sensitivity' | 'preview';
type PanelAssignment = 'performance' | 'capital_stack' | 'none';

interface OutputConfig {
  panel: PanelAssignment;
  displayFormatOverride?: string;
  thresholds?: {
    warn?: { below?: number; above?: number };
    error?: { below?: number; above?: number };
    feedbackText?: string;
    feedbackFix?: string;
  };
  sortOrder: number;
}

interface InputConfig {
  requiredOverride?: boolean;
  hidden: boolean;
}

// Enriched output def — merges NamedRangeInfo + OutputDefinition data
interface OutputDef {
  name: string;
  displayName: string;
  dataType: string;
  category: string;
  displayFormat?: string;
  dependsOn?: string[];
  dependsOnInputs?: string[];
  sensitivityEligible?: boolean;
  description?: string;
}

// Full input def — mirrors InputDefinition from shared-types
interface InputDef {
  name: string;
  displayName: string;
  description: string;
  dataType: string;
  displayFormat: string;
  storageFormat: string;
  category: string;
  required: boolean;
  defaultValue?: unknown;
  priority: string;
  validRange?: {
    min?: number;
    max?: number;
    softMin?: number;
    softMax?: number;
  };
  affects: string[];
  benchmarkSource?: string;
}

interface ValidationRule {
  id: string;
  field: string;
  condition: string;
  severity: string;
  action: string;
  message: string;
  suggestedFix?: string;
  parameters?: Record<string, unknown>;
}

// 2026-05-27 — locally-declared table-input shapes mirroring shared-types
// `TableInputDefinition` (+ its `TableColumnDef`/`TableRowDef`/`AggregateInputDef`
// children). Local declarations keep the existing pattern this file uses for
// `InputDef` (also a manual mirror) — avoids dragging the shared-types Zod
// runtime into this component file when only the static type shape is needed.
interface TableColumnDef {
  key: string;
  displayName: string;
  dataType: string;
  cell: string;
}
interface TableRowDef {
  index: number;
  label: string;
  cells: Record<string, string>;
  defaultValues: Record<string, unknown>;
  active: boolean;
}
interface AggregateInputDef {
  name: string;
  displayName: string;
  targetColumn: string;
  formulaCell: string;
  aggregation: 'sum' | 'weighted_average';
  weightColumn?: string;
}
interface TableInputDefinition {
  name: string;
  displayName: string;
  sheet: string;
  description: string;
  topLeftCell: string;
  bottomRightCell: string;
  rowHeaderColumn?: string;
  colHeaderRow?: number;
  columns: TableColumnDef[];
  rows: TableRowDef[];
  maxRows: number;
  aggregates: AggregateInputDef[];
}

// ─── Priority badge (from ModelSchemaViewer pattern) ──────────────

const PRIORITY_CLASSES: Record<string, string> = {
  critical: 'text-danger bg-danger/7 border-danger/13',
  high: 'text-warn bg-warn/7 border-warn/13',
  medium: 'text-blue bg-blue/7 border-blue/13',
  low: 'text-text-dim bg-input-bg border-panel-border',
};

function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span
      className={clsx(
        'inline-block px-1.5 py-px rounded-[2px] text-micro font-mono font-semibold tracking-[0.3px] uppercase border',
        PRIORITY_CLASSES[priority] || PRIORITY_CLASSES.low
      )}
    >
      {priority}
    </span>
  );
}

// ─── Validation rules display ─────────────────────────────────────

function ValidationRulesDisplay({ rules }: { rules: ValidationRule[] }) {
  if (rules.length === 0)
    return (
      <span className="text-micro font-mono text-text-dim">
        No validation rules
      </span>
    );
  const icons: Record<string, string> = {
    error: 'text-danger',
    warning: 'text-warn',
    info: 'text-blue',
  };
  const symbols: Record<string, string> = {
    error: '●',
    warning: '▲',
    info: 'ℹ',
  };
  return (
    <div className="space-y-1">
      {rules.map((r) => (
        <div
          key={r.id}
          className="flex items-start gap-1.5 text-micro font-mono"
        >
          <span className={icons[r.severity] ?? 'text-text-dim'}>
            {symbols[r.severity] ?? '·'}
          </span>
          <span className="text-text-muted">{r.message}</span>
          {r.action === 'block' && (
            <span className="text-danger ml-1">[blocks]</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Valid range display ──────────────────────────────────────────

function ValidRangeDisplay({
  range,
}: {
  range?: { min?: number; max?: number; softMin?: number; softMax?: number };
}) {
  if (
    !range ||
    (range.min == null &&
      range.max == null &&
      range.softMin == null &&
      range.softMax == null)
  ) {
    return <span className="text-text-dim">Not configured</span>;
  }
  const parts: string[] = [];
  if (range.min != null) parts.push(`min: ${range.min}`);
  if (range.max != null) parts.push(`max: ${range.max}`);
  if (range.softMin != null) parts.push(`soft min: ${range.softMin}`);
  if (range.softMax != null) parts.push(`soft max: ${range.softMax}`);
  return <span className="text-text-muted">{parts.join(' · ')}</span>;
}

// ─── Format default value for display ─────────────────────────────

function formatDefault(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1_000_000)
      return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return value.toLocaleString();
    if (Math.abs(value) < 1 && value !== 0)
      return `${(value * 100).toFixed(1)}%`;
    return String(value);
  }
  return String(value);
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function ModelConfigurator({
  modelId,
  onClose,
}: ModelConfiguratorProps) {
  const { data: modelData, isLoading } = useModel(modelId);
  const updateConfig = useUpdateModelConfig();
  const schema = modelData?.data;

  const [tab, setTab] = useState<Tab>('outputs');
  const [saved, setSaved] = useState(false);

  // Outputs state
  const [outputOverrides, setOutputOverrides] = useState<
    Record<string, OutputConfig>
  >({});
  const [keyMetricNames, setKeyMetricNames] = useState<string[]>([]);

  // Inputs state
  const [inputCollectionOrder, setInputCollectionOrder] = useState<string[]>(
    []
  );
  const [inputOverrides, setInputOverrides] = useState<
    Record<string, InputConfig>
  >({});

  // Description
  const [description, setDescription] = useState('');

  // Sensitivity state
  const [sensitivityOverrides, setSensitivityOverrides] = useState<
    Record<
      string,
      {
        enabled?: boolean;
        displayNameOverride?: string;
        defaultStepSize?: number;
        steps?: number;
        method?: 'absolute' | 'relative';
      }
    >
  >({});
  const [customSensitivityTests, setCustomSensitivityTests] = useState<
    Array<{
      id: string;
      name: string;
      inputNamedRange: string;
      outputNamedRanges: string[];
      method: 'absolute' | 'relative';
      defaultStepSize: number;
      steps: number;
    }>
  >([]);

  // 2026-05-27 — addressable input total = scalar inputs + Σ(table cells).
  // Mirrors the ingest-time `model_registry.addressable_input_count` so the
  // Configurator header reflects the same count the builder + admin grid
  // show. Pre-fix the header was `schema.inputs.length` only (= 46 on
  // multifamily-v1), silently undercounting the 25 table cells.
  const inputCountBreakdown = useMemo(() => {
    if (!schema) return { scalar: 0, tableCells: 0, total: 0 };
    const scalar = schema.inputs.length;
    const tableCells = (schema.tableInputs ?? []).reduce(
      (sum, t) => sum + t.rows.length * t.columns.length,
      0
    );
    return { scalar, tableCells, total: scalar + tableCells };
  }, [schema]);

  // All configurable named ranges (outputs + intermediates — everything except inputs)
  // Enriched with OutputDefinition data where available
  const allConfigurableRanges = useMemo(() => {
    if (!schema) return [];
    const inputNames = new Set(schema.inputs.map((i) => i.name));
    const outputMap = new Map(schema.outputs.map((o) => [o.name, o]));
    return (schema.allNamedRanges ?? [])
      .filter((r) => !inputNames.has(r.name))
      .map((r) => {
        const od = outputMap.get(r.name);
        return {
          name: r.name,
          displayName: r.displayName,
          dataType:
            od?.dataType ??
            ((r as Record<string, unknown>).dataType as string) ??
            'unknown',
          category:
            od?.category ??
            ((r as Record<string, unknown>).category as string) ??
            'other',
          displayFormat: od?.displayFormat ?? r.displayFormat,
          dependsOn: od?.dependsOn,
          dependsOnInputs: od?.dependsOnInputs,
          sensitivityEligible: od?.sensitivityEligible,
          description: od?.description,
        };
      });
  }, [schema]);

  // Validation rules lookup by field name
  const validationRulesByField = useMemo(() => {
    if (!schema) return new Map<string, ValidationRule[]>();
    const map = new Map<string, ValidationRule[]>();
    for (const rule of schema.validationRules ?? []) {
      const existing = map.get(rule.field) || [];
      existing.push(rule as ValidationRule);
      map.set(rule.field, existing);
    }
    return map;
  }, [schema]);

  // Initialize from schema
  useEffect(() => {
    if (!schema) return;
    const dc = schema.displayConfig;

    // Output overrides
    const overrides: Record<string, OutputConfig> = {};
    for (const r of allConfigurableRanges) {
      const existing = dc?.outputOverrides?.[r.name];
      overrides[r.name] = {
        panel: existing?.panel ?? 'none',
        displayFormatOverride: existing?.displayFormatOverride,
        thresholds: existing?.thresholds,
        sortOrder: existing?.sortOrder ?? 0,
      };
    }
    if (dc?.performanceOutputs?.length) {
      for (const name of dc.performanceOutputs) {
        if (overrides[name]) overrides[name].panel = 'performance';
      }
    }
    if (dc?.capitalStackOutputs?.length) {
      for (const name of dc.capitalStackOutputs) {
        if (overrides[name]) overrides[name].panel = 'capital_stack';
      }
    }
    setOutputOverrides(overrides);
    setKeyMetricNames(dc?.keyMetricNames?.length ? dc.keyMetricNames : []);

    // Input overrides
    const inputOv: Record<string, InputConfig> = {};
    for (const inp of schema.inputs) {
      const existing = dc?.inputOverrides?.[inp.name];
      inputOv[inp.name] = {
        requiredOverride: existing?.requiredOverride,
        hidden: existing?.hidden ?? false,
      };
    }
    setInputOverrides(inputOv);
    const order = dc?.inputCollectionOrder?.length
      ? dc.inputCollectionOrder
      : schema.inputs.map((i) => i.name);
    setInputCollectionOrder(order);
    setDescription(dc?.description ?? '');
    setSensitivityOverrides(dc?.sensitivityOverrides ?? {});
    setCustomSensitivityTests(dc?.customSensitivityTests ?? []);
  }, [schema, allConfigurableRanges]);

  // Derived lists
  const perfOutputs = useMemo(
    () =>
      allConfigurableRanges.filter(
        (o) => outputOverrides[o.name]?.panel === 'performance'
      ),
    [allConfigurableRanges, outputOverrides]
  );
  const capitalOutputs = useMemo(
    () =>
      allConfigurableRanges.filter(
        (o) => outputOverrides[o.name]?.panel === 'capital_stack'
      ),
    [allConfigurableRanges, outputOverrides]
  );

  const setPanel = useCallback((name: string, panel: PanelAssignment) => {
    setOutputOverrides((prev) => ({
      ...prev,
      [name]: { ...prev[name], panel },
    }));
    setSaved(false);
  }, []);

  const toggleKeyMetric = useCallback((name: string) => {
    setKeyMetricNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
    setSaved(false);
  }, []);

  const toggleHidden = useCallback((name: string) => {
    setInputOverrides((prev) => ({
      ...prev,
      [name]: { ...prev[name], hidden: !prev[name]?.hidden },
    }));
    setSaved(false);
  }, []);

  const toggleRequired = useCallback((name: string) => {
    setInputOverrides((prev) => {
      const current = prev[name];
      return {
        ...prev,
        [name]: {
          ...current,
          hidden: current?.hidden ?? false,
          requiredOverride: current?.requiredOverride ? undefined : true,
        },
      };
    });
    setSaved(false);
  }, []);

  const reorderInput = useCallback((fromName: string, toName: string) => {
    setInputCollectionOrder((prev) => {
      const fromIdx = prev.indexOf(fromName);
      const toIdx = prev.indexOf(toName);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromName);
      return next;
    });
    setSaved(false);
  }, []);

  const toggleSensitivityEnabled = useCallback(
    (testId: string, enabled: boolean) => {
      setSensitivityOverrides((prev) => ({
        ...prev,
        [testId]: { ...prev[testId], enabled },
      }));
      setSaved(false);
    },
    []
  );

  const overrideSensitivityParam = useCallback(
    (testId: string, field: string, value: unknown) => {
      setSensitivityOverrides((prev) => ({
        ...prev,
        [testId]: { ...prev[testId], [field]: value },
      }));
      setSaved(false);
    },
    []
  );

  const addCustomSensitivityTest = useCallback(
    (test: (typeof customSensitivityTests)[number]) => {
      setCustomSensitivityTests((prev) => [...prev, test]);
      setSaved(false);
    },
    []
  );

  const removeCustomSensitivityTest = useCallback((testId: string) => {
    setCustomSensitivityTests((prev) => prev.filter((t) => t.id !== testId));
    setSensitivityOverrides((prev) => {
      const next = { ...prev };
      delete next[testId];
      return next;
    });
    setSaved(false);
  }, []);

  const handleSave = () => {
    const perfNames = Object.entries(outputOverrides)
      .filter(([, v]) => v.panel === 'performance')
      .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
      .map(([k]) => k);
    const capNames = Object.entries(outputOverrides)
      .filter(([, v]) => v.panel === 'capital_stack')
      .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
      .map(([k]) => k);

    const cleanOverrides: Record<string, OutputConfig> = {};
    for (const [name, ov] of Object.entries(outputOverrides)) {
      if (ov.panel !== 'none' || ov.thresholds || ov.displayFormatOverride) {
        cleanOverrides[name] = ov;
      }
    }

    const cleanInputOverrides: Record<string, InputConfig> = {};
    for (const [name, ov] of Object.entries(inputOverrides)) {
      if (ov.hidden || ov.requiredOverride !== undefined) {
        cleanInputOverrides[name] = ov;
      }
    }

    // Clean sensitivity overrides — only include entries with actual overrides
    const cleanSensOverrides: Record<string, Record<string, unknown>> = {};
    for (const [id, ov] of Object.entries(sensitivityOverrides)) {
      if (
        ov.enabled !== undefined ||
        ov.displayNameOverride ||
        ov.defaultStepSize !== undefined ||
        ov.steps !== undefined ||
        ov.method
      ) {
        cleanSensOverrides[id] = ov;
      }
    }

    updateConfig.mutate(
      {
        id: modelId,
        config: {
          performanceOutputs: perfNames,
          capitalStackOutputs: capNames,
          outputOverrides: cleanOverrides,
          inputOverrides: cleanInputOverrides,
          inputCollectionOrder,
          keyMetricNames,
          description,
          sensitivityOverrides: cleanSensOverrides,
          customSensitivityTests,
        },
      },
      { onSuccess: () => setSaved(true) }
    );
  };

  if (isLoading || !schema) {
    return (
      <div className="fixed inset-0 bg-void/70 z-50 flex items-center justify-center">
        <div className="bg-panel-bg border border-panel-border rounded-[3px] p-6">
          <span className="text-sm font-mono text-text-muted">
            Loading model schema...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-void/70 z-50 flex items-center justify-center overflow-y-auto py-8">
      <div className="bg-panel-bg border border-panel-border rounded-[3px] w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-panel-border flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-mono font-semibold text-text-bright">
              Configure Model
            </h2>
            {/* 2026-05-27 — header count matches the Builder Variables Panel's
                EDITABLE label exactly: a single `{totalInputs}` derived as
                `inputs.length + Σ(tableInputs[i].rows × cols)`. The earlier
                "(46 scalar + 25 table cells)" breakdown read like the table
                cells were a different kind of input — they're not, they're
                just grouped. Match the panel's single-number labeling. */}
            <p className="text-xs font-mono text-text-dim mt-1">
              {schema.name} v{schema.version} — {inputCountBreakdown.total}{' '}
              inputs, {allConfigurableRanges.length} named ranges
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-sm font-mono text-text-dim hover:text-text-muted cursor-pointer bg-transparent border-none"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-panel-border shrink-0">
          {(['outputs', 'inputs', 'sensitivity', 'preview'] as const).map(
            (t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={clsx(
                  'px-5 py-2.5 text-xs font-mono font-semibold tracking-[0.8px] cursor-pointer bg-transparent border-none border-b-2 transition-colors',
                  tab === t
                    ? 'text-accent border-accent'
                    : 'text-text-dim border-transparent hover:text-text-muted'
                )}
              >
                {t.toUpperCase()}
              </button>
            )
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'outputs' && (
            <OutputsTab
              outputs={allConfigurableRanges}
              overrides={outputOverrides}
              keyMetrics={keyMetricNames}
              validationRules={validationRulesByField}
              onSetPanel={setPanel}
              onToggleKeyMetric={toggleKeyMetric}
            />
          )}
          {tab === 'inputs' && (
            <InputsTab
              inputs={schema.inputs as InputDef[]}
              tableInputs={schema.tableInputs ?? []}
              order={inputCollectionOrder}
              overrides={inputOverrides}
              validationRules={validationRulesByField}
              description={description}
              onToggleHidden={toggleHidden}
              onToggleRequired={toggleRequired}
              onReorder={reorderInput}
              onDescriptionChange={(v) => {
                setDescription(v);
                setSaved(false);
              }}
            />
          )}
          {tab === 'sensitivity' && (
            <SensitivityTab
              tests={[
                ...(schema.sensitivityTests ?? []),
                ...customSensitivityTests,
              ]}
              overrides={sensitivityOverrides}
              inputs={
                schema.inputs as Array<{
                  name: string;
                  displayName: string;
                  dataType: string;
                }>
              }
              outputs={allConfigurableRanges}
              customTests={customSensitivityTests}
              onToggleEnabled={toggleSensitivityEnabled}
              onOverrideParam={overrideSensitivityParam}
              onAddCustomTest={addCustomSensitivityTest}
              onRemoveCustomTest={removeCustomSensitivityTest}
            />
          )}
          {tab === 'preview' && (
            <PreviewTab
              perfOutputs={perfOutputs}
              capitalOutputs={capitalOutputs}
              keyMetrics={keyMetricNames}
              allRanges={allConfigurableRanges}
              inputCount={schema.inputs.length}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-panel-border flex items-center justify-between shrink-0">
          <div className="text-xs font-mono text-text-dim">
            {perfOutputs.length} performance · {capitalOutputs.length} capital
            stack · {keyMetricNames.length} key metrics
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-xs font-mono text-accent">Saved ✓</span>
            )}
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={updateConfig.isPending}
            >
              {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// OUTPUTS TAB
// ═══════════════════════════════════════════════════════════════════

function OutputsTab({
  outputs,
  overrides,
  keyMetrics,
  validationRules,
  onSetPanel,
  onToggleKeyMetric,
}: {
  outputs: OutputDef[];
  overrides: Record<string, OutputConfig>;
  keyMetrics: string[];
  validationRules: Map<string, ValidationRule[]>;
  onSetPanel: (name: string, panel: PanelAssignment) => void;
  onToggleKeyMetric: (name: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, OutputDef[]>();
    for (const o of outputs) {
      const cat = o.category || 'other';
      const existing = map.get(cat);
      if (existing) existing.push(o);
      else map.set(cat, [o]);
    }
    return map;
  }, [outputs]);

  return (
    <div className="space-y-5">
      <p className="text-xs font-mono text-text-dim">
        Assign to Performance or Capital Stack panels. Click a row for details.
      </p>

      {Array.from(grouped.entries()).map(([category, items]) => (
        <div key={category}>
          <h3 className="text-micro font-mono font-semibold text-text-muted tracking-[1px] mb-2 uppercase">
            {category}
          </h3>
          <div className="space-y-1">
            {items.map((o) => {
              const ov = overrides[o.name];
              const isExpanded = expanded.has(o.name);
              const rules = validationRules.get(o.name) ?? [];
              return (
                <div
                  key={o.name}
                  className="rounded-[2px] bg-input-bg border border-panel-border overflow-hidden"
                >
                  {/* Compact row */}
                  <div
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    onClick={() => toggle(o.name)}
                  >
                    <span
                      className={clsx(
                        'text-micro font-mono transition-transform',
                        isExpanded && 'rotate-90'
                      )}
                    >
                      ▸
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-mono text-text-bright truncate">
                        {o.displayName}
                      </div>
                      <div className="text-micro font-mono text-text-dim">
                        {o.name} · {o.dataType}
                        {o.displayFormat && <span> · {o.displayFormat}</span>}
                        {o.sensitivityEligible && (
                          <span className="text-accent ml-1.5">SENS</span>
                        )}
                      </div>
                    </div>

                    {/* Panel assignment */}
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(['none', 'performance', 'capital_stack'] as const).map(
                        (p) => (
                          <button
                            key={p}
                            onClick={() => onSetPanel(o.name, p)}
                            className={clsx(
                              'px-2 py-1 text-micro font-mono rounded-[2px] cursor-pointer border transition-colors',
                              ov?.panel === p
                                ? p === 'performance'
                                  ? 'bg-accent-dim border-accent/20 text-accent'
                                  : p === 'capital_stack'
                                  ? 'bg-blue-dim border-blue/20 text-blue'
                                  : 'bg-panel-bg border-panel-border text-text-muted'
                                : 'bg-transparent border-transparent text-text-dim hover:text-text-muted'
                            )}
                          >
                            {p === 'none'
                              ? '—'
                              : p === 'performance'
                              ? 'PERF'
                              : 'CAP'}
                          </button>
                        )
                      )}
                    </div>

                    {/* Key metric toggle */}
                    <label
                      className="flex items-center gap-1.5 cursor-pointer"
                      title="Key metric"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={keyMetrics.includes(o.name)}
                        onChange={() => onToggleKeyMetric(o.name)}
                        className="accent-[var(--color-accent)]"
                      />
                      <span className="text-micro font-mono text-text-dim">
                        KM
                      </span>
                    </label>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-panel-border/50 space-y-2">
                      {o.description && (
                        <div className="text-xs font-mono text-text-muted">
                          {o.description}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-micro font-mono">
                        <div>
                          <span className="text-text-dim">Format:</span>{' '}
                          <span className="text-text-muted">
                            {o.displayFormat || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-text-dim">Sensitivity:</span>{' '}
                          <span
                            className={
                              o.sensitivityEligible
                                ? 'text-accent'
                                : 'text-text-muted'
                            }
                          >
                            {o.sensitivityEligible ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                      {o.dependsOn && o.dependsOn.length > 0 && (
                        <div className="text-micro font-mono">
                          <span className="text-text-dim">Depends on: </span>
                          <span className="text-text-muted">
                            {o.dependsOn.slice(0, 8).join(', ')}
                            {o.dependsOn.length > 8
                              ? ` +${o.dependsOn.length - 8} more`
                              : ''}
                          </span>
                        </div>
                      )}
                      {o.dependsOnInputs && o.dependsOnInputs.length > 0 && (
                        <div className="text-micro font-mono">
                          <span className="text-text-dim">Input deps: </span>
                          <span className="text-text-muted">
                            {o.dependsOnInputs.slice(0, 8).join(', ')}
                            {o.dependsOnInputs.length > 8
                              ? ` +${o.dependsOnInputs.length - 8} more`
                              : ''}
                          </span>
                        </div>
                      )}
                      <div className="text-micro font-mono">
                        <span className="text-text-dim">Validation: </span>
                        <ValidationRulesDisplay rules={rules} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// INPUTS TAB
// ═══════════════════════════════════════════════════════════════════

function InputsTab({
  inputs,
  tableInputs,
  order,
  overrides,
  validationRules,
  description,
  onToggleHidden,
  onToggleRequired,
  onReorder,
  onDescriptionChange,
}: {
  inputs: InputDef[];
  tableInputs: TableInputDefinition[];
  order: string[];
  overrides: Record<string, InputConfig>;
  validationRules: Map<string, ValidationRule[]>;
  description: string;
  onToggleHidden: (name: string) => void;
  onToggleRequired: (name: string) => void;
  onReorder: (fromName: string, toName: string) => void;
  onDescriptionChange: (value: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Drag state
  const [draggedName, setDraggedName] = useState<string | null>(null);
  const [dragOverName, setDragOverName] = useState<string | null>(null);

  const inputMap = useMemo(
    () => new Map(inputs.map((i) => [i.name, i])),
    [inputs]
  );

  const orderedInputs = useMemo(
    () => order.map((name) => inputMap.get(name)).filter(Boolean) as InputDef[],
    [order, inputMap]
  );

  const requiredInputs = useMemo(
    () =>
      orderedInputs.filter(
        (i) => i.required || overrides[i.name]?.requiredOverride === true
      ),
    [orderedInputs, overrides]
  );

  const optionalInputs = useMemo(
    () =>
      orderedInputs.filter(
        (i) => !i.required && overrides[i.name]?.requiredOverride !== true
      ),
    [orderedInputs, overrides]
  );

  const handleDragStart = (name: string) => setDraggedName(name);
  const handleDragOver = (e: React.DragEvent, name: string) => {
    e.preventDefault();
    setDragOverName(name);
  };
  const handleDrop = (targetName: string) => {
    if (draggedName && draggedName !== targetName) {
      onReorder(draggedName, targetName);
    }
    setDraggedName(null);
    setDragOverName(null);
  };
  const handleDragEnd = () => {
    setDraggedName(null);
    setDragOverName(null);
  };

  return (
    <div className="space-y-5">
      {/* Description */}
      <div>
        <label className="block text-xs font-mono font-semibold text-text-muted tracking-[0.8px] mb-1.5">
          MODEL DESCRIPTION
        </label>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={2}
          className="w-full bg-input-bg border border-panel-border rounded-[2px] px-3 py-2 text-sm font-mono text-text-bright placeholder:text-text-dim focus:border-accent/30 focus:outline-none resize-none"
          placeholder="Brief description of this model..."
        />
      </div>

      {/* Required section */}
      <div>
        <h3 className="text-xs font-mono font-semibold text-warn tracking-[0.8px] mb-1">
          REQUIRED ({requiredInputs.length})
        </h3>
        <p className="text-micro font-mono text-text-dim mb-2">
          Collected first. Drag to reorder. Click a row for details.
        </p>
        <div className="space-y-0.5">
          {requiredInputs.map((inp, idx) => (
            <InputRow
              key={inp.name}
              input={inp}
              index={idx}
              override={overrides[inp.name]}
              rules={validationRules.get(inp.name) ?? []}
              isExpanded={expanded.has(inp.name)}
              isDragging={draggedName === inp.name}
              isDragOver={dragOverName === inp.name}
              onToggleExpand={() => toggle(inp.name)}
              onDragStart={() => handleDragStart(inp.name)}
              onDragOver={(e) => handleDragOver(e, inp.name)}
              onDrop={() => handleDrop(inp.name)}
              onDragEnd={handleDragEnd}
              onToggleHidden={onToggleHidden}
              onToggleRequired={onToggleRequired}
            />
          ))}
        </div>
      </div>

      {/* Optional section */}
      <div>
        <h3 className="text-xs font-mono font-semibold text-text-muted tracking-[0.8px] mb-1">
          OPTIONAL ({optionalInputs.length})
        </h3>
        <p className="text-micro font-mono text-text-dim mb-2">
          Collected after required inputs. Hidden inputs are skipped.
        </p>
        <div className="space-y-0.5">
          {optionalInputs.map((inp, idx) => (
            <InputRow
              key={inp.name}
              input={inp}
              index={idx}
              override={overrides[inp.name]}
              rules={validationRules.get(inp.name) ?? []}
              isExpanded={expanded.has(inp.name)}
              isDragging={draggedName === inp.name}
              isDragOver={dragOverName === inp.name}
              onToggleExpand={() => toggle(inp.name)}
              onDragStart={() => handleDragStart(inp.name)}
              onDragOver={(e) => handleDragOver(e, inp.name)}
              onDrop={() => handleDrop(inp.name)}
              onDragEnd={handleDragEnd}
              onToggleHidden={onToggleHidden}
              onToggleRequired={onToggleRequired}
            />
          ))}
        </div>
      </div>

      {/* 2026-05-27 — Tables section. Each table input is a structured group
          (one card per table) showing the columns / rows / aggregates so the
          admin can see ALL addressable inputs at a glance. Read-only first
          increment — per-table admin overrides (visibility, required, row
          activity) are a deferred follow-up that needs display_config schema
          extension. */}
      {tableInputs.length > 0 && <TablesSection tables={tableInputs} />}
    </div>
  );
}

// ─── Tables Section ──────────────────────────────────────────────
// 2026-05-27 — one card per `TableInputDefinition`. Each card shows the
// columns + rows + aggregates so the admin can audit the full addressable
// input set (was missing from this dialog entirely — the count in the
// header was undercounting by ~25 cells per multifamily-v1 model).

function TablesSection({ tables }: { tables: TableInputDefinition[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const totalTableCells = useMemo(
    () => tables.reduce((sum, t) => sum + t.rows.length * t.columns.length, 0),
    [tables]
  );

  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-mono font-semibold text-text-muted tracking-[0.8px]">
          TABLES ({tables.length})
        </h3>
        <span className="text-micro font-mono text-text-dim">
          {totalTableCells} addressable cells
        </span>
      </div>
      <p className="text-micro font-mono text-text-dim mb-3">
        Multi-row structured inputs. The agent collects each table&apos;s cells
        as a single grouped prompt (e.g. UnitMix asks for studio + 1BR + 2BR
        unit counts + rents together).
      </p>
      <div className="space-y-2">
        {tables.map((t) => {
          const cellCount = t.rows.length * t.columns.length;
          const isOpen = expanded.has(t.name);
          return (
            <div
              key={t.name}
              className="border border-panel-border rounded-[3px] bg-panel-bg"
              data-testid={`table-input-card-${t.name}`}
            >
              <button
                type="button"
                onClick={() => toggle(t.name)}
                className="w-full flex items-center justify-between px-3 py-2 bg-transparent border-none cursor-pointer text-left hover:bg-accent-faint/30 transition-colors"
                aria-expanded={isOpen}
              >
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="text-sm font-mono font-medium text-text-bright truncate">
                    {t.displayName || t.name}
                  </span>
                  <span className="text-micro font-mono text-text-dim tabular-nums shrink-0">
                    {t.rows.length} rows × {t.columns.length} cols = {cellCount}{' '}
                    cells
                  </span>
                  <span className="text-micro font-mono text-text-dim shrink-0">
                    · {t.sheet}!{t.topLeftCell}:{t.bottomRightCell}
                  </span>
                </div>
                <span className="text-xs font-mono text-text-dim shrink-0 ml-3">
                  {isOpen ? '▾' : '▸'}
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-panel-border px-3 py-3 space-y-3">
                  {t.description && (
                    <p className="text-micro font-mono text-text-muted italic">
                      {t.description}
                    </p>
                  )}

                  {/* Columns */}
                  <div>
                    <h4 className="text-micro font-mono font-semibold text-text-dim tracking-[0.6px] mb-1">
                      COLUMNS ({t.columns.length})
                    </h4>
                    <div className="space-y-0.5">
                      {t.columns.map((c) => (
                        <div
                          key={c.key}
                          className="flex items-baseline gap-3 text-xs font-mono px-2 py-1 bg-void/30 rounded-[2px]"
                        >
                          <span className="text-text-bright min-w-[140px]">
                            {c.displayName}
                          </span>
                          <span className="text-accent text-micro">
                            {c.dataType}
                          </span>
                          <span className="text-text-dim text-micro ml-auto">
                            {c.cell}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rows */}
                  <div>
                    <h4 className="text-micro font-mono font-semibold text-text-dim tracking-[0.6px] mb-1">
                      ROWS ({t.rows.length}
                      {t.maxRows > t.rows.length ? ` of ${t.maxRows} max` : ''})
                    </h4>
                    <div className="space-y-0.5">
                      {t.rows.map((r) => {
                        const defaultsCount = Object.keys(
                          r.defaultValues ?? {}
                        ).length;
                        return (
                          <div
                            key={r.index}
                            className="flex items-baseline gap-3 text-xs font-mono px-2 py-1 bg-void/30 rounded-[2px]"
                          >
                            <span className="text-text-bright min-w-[140px]">
                              {r.label}
                            </span>
                            {!r.active && (
                              <span className="text-micro text-text-dim">
                                inactive
                              </span>
                            )}
                            <span className="text-text-dim text-micro ml-auto">
                              {defaultsCount > 0
                                ? `${defaultsCount} default${
                                    defaultsCount === 1 ? '' : 's'
                                  }`
                                : 'no defaults'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Aggregates */}
                  {t.aggregates.length > 0 && (
                    <div>
                      <h4 className="text-micro font-mono font-semibold text-text-dim tracking-[0.6px] mb-1">
                        AGGREGATES ({t.aggregates.length})
                      </h4>
                      <div className="space-y-0.5">
                        {t.aggregates.map((a) => (
                          <div
                            key={a.name}
                            className="flex items-baseline gap-3 text-xs font-mono px-2 py-1 bg-void/30 rounded-[2px]"
                          >
                            <span className="text-text-bright min-w-[140px]">
                              {a.displayName}
                            </span>
                            <span className="text-accent text-micro">
                              {a.aggregation}
                            </span>
                            <span className="text-text-dim text-micro">
                              → {a.targetColumn}
                            </span>
                            <span className="text-text-dim text-micro ml-auto">
                              {a.formulaCell}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Single Input Row ─────────────────────────────────────────────

function InputRow({
  input: inp,
  index: idx,
  override: ov,
  rules,
  isExpanded,
  isDragging,
  isDragOver,
  onToggleExpand,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onToggleHidden,
  onToggleRequired,
}: {
  input: InputDef;
  index: number;
  override?: InputConfig;
  rules: ValidationRule[];
  isExpanded: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onToggleExpand: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onToggleHidden: (name: string) => void;
  onToggleRequired: (name: string) => void;
}) {
  const isSchemaRequired = inp.required;
  const isAdminRequired = !inp.required && ov?.requiredOverride === true;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={clsx(
        'rounded-[2px] border transition-colors overflow-hidden',
        ov?.hidden
          ? 'bg-void border-panel-border opacity-50'
          : 'bg-input-bg border-panel-border',
        isDragging && 'opacity-40',
        isDragOver && 'border-accent/40'
      )}
    >
      {/* Compact row */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer"
        onClick={onToggleExpand}
      >
        <span
          className={clsx(
            'text-micro font-mono transition-transform',
            isExpanded && 'rotate-90'
          )}
        >
          ▸
        </span>

        {/* Drag handle + order number */}
        <span
          className="text-micro font-mono text-text-dim cursor-grab active:cursor-grabbing select-none"
          title="Drag to reorder"
        >
          ⠿
        </span>
        <span className="text-micro font-mono text-text-dim w-5 text-right tabular-nums">
          {idx + 1}
        </span>

        {/* Input info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-text-bright truncate">
              {inp.displayName}
            </span>
            {isSchemaRequired && (
              <span className="px-1.5 py-px rounded-[2px] text-micro font-mono font-semibold text-warn bg-warn/7 border border-warn/13">
                REQ
              </span>
            )}
            {isAdminRequired && (
              <span className="px-1.5 py-px rounded-[2px] text-micro font-mono font-semibold text-accent bg-accent-dim border border-accent/15">
                REQ*
              </span>
            )}
            <PriorityBadge priority={inp.priority} />
          </div>
          <div className="text-micro font-mono text-text-dim">
            {inp.name} · {inp.dataType}
            {inp.displayFormat && <span> · {inp.displayFormat}</span>}
            {inp.defaultValue !== undefined && (
              <span className="ml-1.5 text-text-muted">
                = {formatDefault(inp.defaultValue)}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div
          className="flex items-center gap-3 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Required toggle */}
          <button
            onClick={() => onToggleRequired(inp.name)}
            disabled={isSchemaRequired}
            title={
              isSchemaRequired
                ? 'Schema-required (cannot change)'
                : isAdminRequired
                ? 'Remove required override'
                : 'Mark as required'
            }
            className={clsx(
              'px-2 py-1 text-micro font-mono rounded-[2px] cursor-pointer border transition-colors',
              isSchemaRequired
                ? 'text-warn/50 border-warn/10 cursor-not-allowed'
                : isAdminRequired
                ? 'text-accent bg-accent-dim border-accent/15 hover:bg-accent/13'
                : 'text-text-dim border-transparent hover:text-text-muted hover:border-panel-border'
            )}
          >
            REQ
          </button>

          {/* Hidden toggle */}
          <label
            className="flex items-center gap-1.5 cursor-pointer"
            title="Hide from guided collection"
          >
            <input
              type="checkbox"
              checked={ov?.hidden ?? false}
              onChange={() => onToggleHidden(inp.name)}
              className="accent-[var(--color-danger)]"
            />
            <span className="text-micro font-mono text-text-dim">Hide</span>
          </label>
        </div>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-panel-border/50 space-y-2 ml-8">
          {inp.description && (
            <div className="text-xs font-mono text-text-muted">
              {inp.description}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-micro font-mono">
            <div>
              <span className="text-text-dim">Default:</span>{' '}
              <span className="text-text-muted">
                {formatDefault(inp.defaultValue)}
              </span>
            </div>
            <div>
              <span className="text-text-dim">Format:</span>{' '}
              <span className="text-text-muted">
                {inp.displayFormat || '—'}
              </span>
            </div>
            <div>
              <span className="text-text-dim">Priority:</span>{' '}
              <span className="text-text-muted">{inp.priority}</span>
            </div>
            <div>
              <span className="text-text-dim">Storage:</span>{' '}
              <span className="text-text-muted">
                {inp.storageFormat || 'raw'}
              </span>
            </div>
            <div>
              <span className="text-text-dim">Category:</span>{' '}
              <span className="text-text-muted">{inp.category}</span>
            </div>
            {inp.benchmarkSource && (
              <div>
                <span className="text-text-dim">Benchmark:</span>{' '}
                <span className="text-text-muted">{inp.benchmarkSource}</span>
              </div>
            )}
          </div>
          <div className="text-micro font-mono">
            <span className="text-text-dim">Valid range: </span>
            <ValidRangeDisplay range={inp.validRange} />
          </div>
          {inp.affects && inp.affects.length > 0 && (
            <div className="text-micro font-mono">
              <span className="text-text-dim">Affects: </span>
              <span className="text-text-muted">
                {inp.affects.slice(0, 8).join(', ')}
                {inp.affects.length > 8
                  ? ` +${inp.affects.length - 8} more`
                  : ''}
              </span>
            </div>
          )}
          <div className="text-micro font-mono">
            <span className="text-text-dim">Validation: </span>
            <ValidationRulesDisplay rules={rules} />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PREVIEW TAB
// ═══════════════════════════════════════════════════════════════════

function PreviewTab({
  perfOutputs,
  capitalOutputs,
  keyMetrics,
  allRanges,
  inputCount,
}: {
  perfOutputs: OutputDef[];
  capitalOutputs: OutputDef[];
  keyMetrics: string[];
  allRanges: OutputDef[];
  inputCount: number;
}) {
  const rangeMap = useMemo(
    () => new Map(allRanges.map((r) => [r.name, r])),
    [allRanges]
  );

  return (
    <div className="space-y-5">
      <p className="text-xs font-mono text-text-dim">
        Preview of how the Builder's Model Overview panel will display with this
        configuration.
      </p>

      {/* Performance preview */}
      <div className="border border-panel-border rounded-[2px] overflow-hidden">
        <div className="px-4 py-2.5 bg-input-bg border-b border-panel-border">
          <span className="text-micro font-mono text-text-dim tracking-[0.8px]">
            PERFORMANCE
          </span>
        </div>
        {perfOutputs.length === 0 ? (
          <div className="px-4 py-4 text-xs font-mono text-text-dim text-center">
            No outputs assigned
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 p-4">
            {perfOutputs.map((o) => (
              <div key={o.name}>
                <div className="text-micro font-mono text-text-dim">
                  {o.displayName}
                </div>
                <div className="text-lg font-mono font-bold tabular-nums text-accent">
                  —
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Capital Stack preview */}
      <div className="border border-panel-border rounded-[2px] overflow-hidden">
        <div className="px-4 py-2.5 bg-input-bg border-b border-panel-border">
          <span className="text-micro font-mono text-text-dim tracking-[0.8px]">
            CAPITAL STACK
          </span>
        </div>
        {capitalOutputs.length === 0 ? (
          <div className="px-4 py-4 text-xs font-mono text-text-dim text-center">
            No outputs assigned
          </div>
        ) : (
          <div className="space-y-1.5 p-4">
            {capitalOutputs.map((o) => (
              <div key={o.name} className="flex justify-between items-baseline">
                <span className="text-xs font-mono text-text-muted">
                  {o.displayName}
                </span>
                <span className="text-sm font-mono text-text-bright tabular-nums font-medium">
                  —
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Key metrics preview */}
      <div className="border border-panel-border rounded-[2px] overflow-hidden">
        <div className="px-4 py-2.5 bg-input-bg border-b border-panel-border">
          <span className="text-micro font-mono text-text-dim tracking-[0.8px]">
            KEY METRICS (agent tool responses)
          </span>
        </div>
        {keyMetrics.length === 0 ? (
          <div className="px-4 py-4 text-xs font-mono text-text-dim text-center">
            No key metrics selected — will use fallback defaults
          </div>
        ) : (
          <div className="p-4 flex flex-wrap gap-2">
            {keyMetrics.map((name) => {
              const r = rangeMap.get(name);
              return (
                <span
                  key={name}
                  className="px-2 py-1 bg-accent-dim border border-accent/15 rounded-[2px] text-xs font-mono text-accent"
                >
                  {r?.displayName ?? name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Input collection summary */}
      <div className="border border-panel-border rounded-[2px] overflow-hidden">
        <div className="px-4 py-2.5 bg-input-bg border-b border-panel-border">
          <span className="text-micro font-mono text-text-dim tracking-[0.8px]">
            INPUT COLLECTION ({inputCount} total)
          </span>
        </div>
        <div className="px-4 py-3 text-xs font-mono text-text-dim">
          The agent will collect inputs in the configured order. Hidden inputs
          will be skipped.
        </div>
      </div>
    </div>
  );
}
